import { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Layout, Menu, Typography, Button, Grid, Drawer } from 'antd'
import {
  AccountBookOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  BookOutlined,
  SettingOutlined,
  LogoutOutlined,
  VideoCameraOutlined,
  AudioOutlined,
  MenuOutlined,
  PictureOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { useAuthStore } from '../store/auth'

const { Header, Content } = Layout
const { Text } = Typography

type MenuItem = Required<MenuProps>['items'][number]

const menuItems: MenuItem[] = [
  {
    key: '/operations/accounts',
    icon: <AccountBookOutlined />,
    label: '账号管理',
  },
  {
    key: '/operations/contents',
    icon: <FileTextOutlined />,
    label: '内容产出',
  },
  {
    key: '/operations/topics',
    icon: <DatabaseOutlined />,
    label: '选题库',
  },
  {
    key: '/operations/social-commands',
    icon: <BookOutlined />,
    label: 'md文件归档',
  },
  {
    key: '/scripts/submit',
    icon: <AudioOutlined />,
    label: '脚本提取',
  },
  {
    key: '/scripts/tasks',
    icon: <FileTextOutlined />,
    label: '任务列表',
  },
  {
    key: '/scripts/completed',
    icon: <FileTextOutlined />,
    label: '已完成',
  },
  {
    key: '/video-auto',
    icon: <VideoCameraOutlined />,
    label: '视频剪辑',
  },
  {
    key: '/cover-design',
    icon: <PictureOutlined />,
    label: '封面设计',
  },
  {
    key: '/settings',
    icon: <SettingOutlined />,
    label: '设置',
  },
]

function getSelectedKey(pathname: string): string {
  if (pathname.startsWith('/operations/accounts')) return '/operations/accounts'
  if (pathname.startsWith('/operations/contents')) return '/operations/contents'
  if (pathname.startsWith('/operations/topics')) return '/operations/topics'
  if (pathname.startsWith('/operations/social-commands')) return '/operations/social-commands'
  if (pathname.startsWith('/scripts/submit')) return '/scripts/submit'
  if (pathname.startsWith('/scripts/tasks')) return '/scripts/tasks'
  if (pathname.startsWith('/scripts/completed')) return '/scripts/completed'
  if (pathname.startsWith('/video-auto')) return '/video-auto'
  if (pathname.startsWith('/cover-design')) return '/cover-design'
  if (pathname.startsWith('/settings')) return '/settings'
  return '/operations/contents'
}

export default function ShellLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { clearAuth } = useAuthStore()
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    navigate(e.key)
    setDrawerOpen(false)
  }

  const isCoverDesign = location.pathname.startsWith('/cover-design')
  const isSocialCommands = location.pathname.startsWith('/operations/social-commands')
  const noOuterGap = isCoverDesign || isSocialCommands
  const isCompact = mounted && !screens.lg

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          background: '#fff',
          padding: isMobile ? '0 12px' : '0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          gap: 8,
        }}
      >
        {/* 左侧 Logo + 标题 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <img
            src="/boswindor-logo.png"
            alt="Boswindor"
            style={{ height: 28 }}
          />
          {mounted && !isCompact && (
            <Text strong style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
              Boswindor运营管理平台
            </Text>
          )}
        </div>

        {/* 中间 导航 */}
        {mounted && isMobile ? (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
          />
        ) : (
          <Menu
            mode="horizontal"
            className="compact-nav"
            selectedKeys={[getSelectedKey(location.pathname)]}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              flex: 1,
              borderBottom: 0,
              justifyContent: 'center',
              background: 'transparent',
              minWidth: 0,
              fontSize: 13,
            }}
          />
        )}

        {/* 右侧 退出登录 */}
        <Button
          type="text"
          danger
          icon={<LogoutOutlined />}
          onClick={() => {
            clearAuth()
            navigate('/login')
          }}
          style={{ flexShrink: 0, padding: '0 8px' }}
        >
          {mounted && !isCompact ? '退出' : ''}
        </Button>
      </Header>

      {/* 移动端抽屉导航 */}
      <Drawer
        title="菜单"
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={220}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="inline"
          selectedKeys={[getSelectedKey(location.pathname)]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Drawer>

      <Content
        style={{
          margin: noOuterGap ? 0 : (isMobile ? 12 : 24),
          padding: noOuterGap ? 0 : (isMobile ? 12 : 24),
          background: noOuterGap ? 'transparent' : '#fff',
          borderRadius: noOuterGap ? 0 : 8,
        }}
      >
        <Outlet />
      </Content>
    </Layout>
  )
}
